import { products, type ProductEntry } from './data/products';
import { projects, type ProjectEntry } from './data/projects';

export interface SiteRuntime {
  dispose: () => void;
}

const contactEmail = 'support@alldatwater.xyz';

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function pluralize(count: number, singular: string): string {
  return `${count} ${singular}${count === 1 ? '' : 's'}`;
}

function isExternal(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

function renderFolderIcon(): string {
  return `
    <svg class="folder-glyph" viewBox="0 0 48 40" aria-hidden="true" focusable="false">
      <path d="M1.75 8.5A3.75 3.75 0 0 1 5.5 4.75h11.06a3.75 3.75 0 0 1 2.92 1.4l2.3 2.87a3.75 3.75 0 0 0 2.93 1.4H42.5a3.75 3.75 0 0 1 3.75 3.76v17.32a3.75 3.75 0 0 1-3.75 3.75h-37a3.75 3.75 0 0 1-3.75-3.75Z" />
    </svg>
  `;
}

function renderFolder(href: string, name: string, meta: string): string {
  return `
    <a class="folder" href="${escapeHtml(href)}">
      ${renderFolderIcon()}
      <span class="folder-name">${escapeHtml(name)}</span>
      <span class="folder-meta">${escapeHtml(meta)}</span>
    </a>
  `;
}

function renderAppsFolder(): string {
  return `
    <details class="folder folder-menu">
      <summary class="folder-trigger">
        ${renderFolderIcon()}
        <span class="folder-name">Apps</span>
        <span class="folder-meta">${escapeHtml(pluralize(products.length, 'app'))}</span>
      </summary>
      <nav class="folder-options" aria-label="Apps">
        ${products
          .map(
            (product: ProductEntry) =>
              `<a href="/${escapeHtml(product.slug)}/">${escapeHtml(product.name)}</a>`
          )
          .join('')}
      </nav>
    </details>
  `;
}

function renderProjectCard(project: ProjectEntry): string {
  const thumb = project.image
    ? `<img src="${escapeHtml(project.image)}" alt="" />`
    : escapeHtml(project.icon ?? project.name.charAt(0));
  const heading = escapeHtml(project.name);
  const name = project.url
    ? `<a class="project-name-link" href="${escapeHtml(project.url)}"${
        isExternal(project.url) ? ' target="_blank" rel="noopener noreferrer"' : ''
      }>${heading}</a>`
    : heading;
  const tech = project.tech.length
    ? `<ul class="project-tech">${project.tech
        .map((item) => `<li>${escapeHtml(item)}</li>`)
        .join('')}</ul>`
    : '';

  return `
    <article class="project-card">
      <span class="project-thumb" aria-hidden="true">${thumb}</span>
      <h2 class="project-name">${name}</h2>
      <p class="project-status">${escapeHtml(project.status)}</p>
      <p class="project-description">${escapeHtml(project.description)}</p>
      ${tech}
    </article>
  `;
}

function renderHome(): string {
  return `
    <div class="page-shell home-page">
      <header class="site-header">
        <a class="wordmark" href="/" aria-label="All Dat Water home">All Dat Water</a>
      </header>
      <main id="main">
        <nav class="folder-grid" aria-label="Sections">
          ${renderAppsFolder()}
          ${renderFolder('/playground/', 'Playground', pluralize(projects.length, 'project'))}
        </nav>
      </main>
      <footer class="site-footer">
        <span class="footer-contact">${contactEmail}</span>
      </footer>
    </div>
  `;
}

function renderPlayground(): string {
  const body = projects.length
    ? `<div class="project-grid">${projects.map(renderProjectCard).join('')}</div>`
    : `<p class="empty-state">Nothing here yet.</p>`;

  return `
    <main class="page-shell folder-page" id="main">
      <a class="back-link" href="/">&#8592; All Dat Water</a>
      <h1>Playground</h1>
      ${body}
    </main>
  `;
}

function renderNotFound(): string {
  return `
    <main class="page-shell app-page" id="main">
      <a class="back-link" href="/">&#8592; All Dat Water</a>
      <h1>Not found</h1>
    </main>
  `;
}

export function createSite(root: HTMLElement, pathname = window.location.pathname): SiteRuntime {
  const previousTitle = document.title;
  const segments = pathname.split('/').filter(Boolean);
  const [slug] = segments;

  if (!slug) {
    document.title = 'All Dat Water';
    root.innerHTML = renderHome();
  } else if (slug === 'playground' && segments.length === 1) {
    document.title = 'Playground | All Dat Water';
    root.innerHTML = renderPlayground();
  } else {
    document.title = 'Not found | All Dat Water';
    root.innerHTML = renderNotFound();
  }

  return {
    dispose() {
      document.title = previousTitle;
    }
  };
}
